import * as THREE from 'three'
import type { SogsData } from '@/plugins/gsplat/core/SogsData'
import type { SplatData } from '@/plugins/gsplat/core/SplatData'
import type { IGSplatResource } from '@/plugins/gsplat/types/ResourceTypes'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 全局资源 ID 计数器（对齐 PlayCanvas 设计）
 */
let globalResourceId = 0

/**
 * GSplat 资源封装
 * 提供统一接口访问 SplatData 和 SogsData
 *
 * 核心功能：
 * 1. 引用计数管理（用于资源生命周期）
 * 2. 唯一 ID 分配（用于排序器识别）
 * 3. AABB 缓存
 * 4. 统一的属性访问接口
 *
 * @remarks
 * 对应 PlayCanvas 的 GSplatResourceBase 类
 * 主要差异：不依赖 GraphicsDevice，简化 Mesh 管理
 */
export class GSplatResource implements IGSplatResource {
  /**
   * 唯一资源 ID
   */
  readonly id: number = globalResourceId++

  /**
   * 内部数据引用（SplatData 或 SogsData）
   */
  readonly data: SplatData | SogsData

  /**
   * 引用计数（用于跟踪活跃使用）
   */
  private _refCount: number = 0

  /**
   * 缓存的 AABB
   */
  private _aabb: THREE.Box3 | null = null

  /**
   * 缓存的中心点数组
   */
  private _centers: Float32Array | null = null

  /**
   * 创建新的 GSplat 资源
   *
   * @param data - SplatData 或 SogsData 实例
   */
  constructor(data: SplatData | SogsData) {
    this.data = data
  }

  /**
   * 增加引用计数
   */
  incRefCount(): void {
    this._refCount++
  }

  /**
   * 减少引用计数
   */
  decRefCount(): void {
    this._refCount--
    if (this._refCount < 0) {
      log.warn(`GSplatResource[${this.id}]: refCount is negative (${this._refCount})`)
    }
  }

  /**
   * 获取当前引用计数
   *
   * @remarks
   * 表示资源被引擎内部使用的次数
   * 当引用计数非零时，不应卸载资源
   */
  get refCount(): number {
    return this._refCount
  }

  /**
   * 获取 Splat 总数
   */
  get numSplats(): number {
    return this.data.numSplats
  }

  /**
   * 获取包围盒（懒加载）
   */
  get aabb(): THREE.Box3 {
    if (!this._aabb) {
      this._aabb = new THREE.Box3()
      this.data.calcAabb(this._aabb)
    }
    return this._aabb
  }

  /**
   * 获取中心点数组（懒加载）
   * 用于排序器
   */
  get centers(): Float32Array {
    if (!this._centers) {
      this._centers = this.data.getCenters()
    }
    return this._centers
  }

  /**
   * 获取球谐阶数
   */
  get shBands(): number {
    return this.data.shBands
  }

  /**
   * 获取指定属性（仅 SplatData）
   *
   * @param name - 属性名称
   * @returns 属性数据，不存在或不支持时返回 null
   */
  getProp(name: string): Float32Array | Uint8Array | Int32Array | null {
    if ('getProp' in this.data) {
      return this.data.getProp(name)
    }
    return null
  }

  /**
   * 获取元数据（仅 SogsData）
   */
  get meta() {
    if ('meta' in this.data) {
      return this.data.meta
    }
    return null
  }

  /**
   * 清理资源
   */
  destroy(): void {
    if ('dispose' in this.data && typeof this.data.dispose === 'function') {
      this.data.dispose()
    }
    this._aabb = null
    this._centers = null
  }

  /**
   * 清理资源（别名，保持向后兼容）
   * @deprecated 使用 destroy() 替代
   */
  dispose(): void {
    this.destroy()
  }
}
